import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Check, FileText, Loader2, ClipboardCheck, ExternalLink } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PAGE_SEO, breadcrumbLd } from "@/lib/seo";
import { useBrief } from "@/context/BriefContext";
import { getFrameworks, getFieldNotes, submitPacket } from "@/lib/api";
import { track, getUTM } from "@/lib/analytics";
import { amp } from "@/lib/amp";

const COPPER_TEXT = "#8c5740"; // copper-deep, AA for small text
const COPPER_ACCENT = "#a0644b"; // copper accent (rules, large)

const TOPICS = ["All", "Strategy", "AI & governance", "Production", "Operating"];

function useFade() {
  const reduce = useReducedMotion();
  return {
    initial: { opacity: 0, y: reduce ? 0 : 16 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-60px" },
    transition: { duration: reduce ? 0 : 0.6, ease: [0.22, 1, 0.36, 1] },
  };
}

function topicId(t) {
  return t.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
}

function TopicFilter({ value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-2.5 mt-7" data-testid={testid} role="group" aria-label="Filter by topic">
      {TOPICS.map((t) => {
        const active = value === t;
        return (
          <button
            key={t}
            type="button"
            onClick={() => onChange(t)}
            data-testid={`${testid}-${topicId(t)}`}
            aria-pressed={active}
            className={`text-[11px] uppercase tracking-[0.18em] font-normal px-4 py-2 rounded-[2px] border transition-colors duration-300 ${
              active
                ? "border-[color:var(--copper)] text-[color:var(--copper-deep)] bg-[color:var(--copper)]/[0.07]"
                : "border-[color:var(--rule)] text-[color:var(--grey)] hover:border-[color:var(--ink)] hover:text-[color:var(--ink)]"
            }`}
          >
            {t}
          </button>
        );
      })}
    </div>
  );
}

function PacketSignup() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Enter a valid email address.");
      return;
    }
    setStatus("loading");
    try {
      await submitPacket({ email, utm: getUTM() });
      track("email_capture", { source: "operator_packet" });
      setStatus("done");
    } catch (err) {
      setStatus("idle");
      setError(err?.response?.data?.detail ? String(err.response.data.detail) : "Something went wrong. Please try again.");
    }
  };

  if (status === "done") {
    return (
      <div className="flex items-start gap-3" data-testid="packet-signup-success">
        <span className="grid place-items-center h-9 w-9 rounded-full flex-shrink-0" style={{ border: `1px solid ${COPPER_ACCENT}`, color: COPPER_TEXT }}>
          <Check className="h-5 w-5" />
        </span>
        <p className="exec-serif text-[17px] text-[color:var(--lead-ink)]">You are on the list. The next Operating Room lands in your inbox.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="packet-signup-form" noValidate className="max-w-xl">
      <div className="flex flex-col sm:flex-row sm:items-end gap-4">
        <div className="flex-1">
          <label htmlFor="packet-email" className="block text-[11px] uppercase tracking-[0.16em] font-normal text-[color:var(--ink)]">
            Email
          </label>
          <input
            id="packet-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            className="exec-underline"
            data-testid="packet-email-input"
            aria-invalid={!!error}
          />
        </div>
        <button type="submit" disabled={status === "loading"} data-testid="packet-submit" className="exec-btn exec-btn-primary disabled:opacity-60">
          {status === "loading" ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending</> : "Send it to me"}
        </button>
      </div>
      {error && (
        <p className="exec-serif text-[16px] mt-2" style={{ color: "#9a3412" }} role="alert" data-testid="packet-error">
          {error}
        </p>
      )}
    </form>
  );
}

export default function Resources() {
  const fade = useFade();
  const { openBrief } = useBrief();
  const [frameworks, setFrameworks] = useState([]);
  const [fieldNotes, setFieldNotes] = useState([]);
  const [fwTopic, setFwTopic] = useState("All");
  const [noteTopic, setNoteTopic] = useState("All");

  useEffect(() => {
    getFrameworks().then((d) => setFrameworks(d || [])).catch(() => setFrameworks([]));
    getFieldNotes().then((d) => setFieldNotes(d || [])).catch(() => setFieldNotes([]));
  }, []);

  const fmtArticleDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch {
      return "";
    }
  };

  const gridFrameworks = frameworks.filter((f) => !f.links_to_tool);
  const visibleFrameworks = gridFrameworks.filter((f) => fwTopic === "All" || f.topic === fwTopic);
  const visibleNotes = fieldNotes.filter((n) => noteTopic === "All" || n.topic === noteTopic);

  return (
    <div className="exec-scope min-h-screen">
      <Seo {...PAGE_SEO.resources} jsonLd={breadcrumbLd([{ name: "Home", path: "/" }, { name: "Resources", path: "/resources" }])} />
      {/* 1) INTRO */}
      <section className="exec-container pt-24 md:pt-28 pb-4">
        <motion.div {...fade} className="max-w-[820px]">
          <p className="exec-eyebrow">Resources</p>
          <h1 className="font-extralight text-[clamp(36px,5vw,64px)] leading-[1.08] mt-5">
            An owned library, not a blog.
          </h1>
          <p className="exec-serif text-[clamp(18px,2vw,22px)] leading-[1.5] text-[color:var(--lead-ink)] mt-6">
            The frameworks we use, the work we can show, a readiness diagnostic that maps where you
            stand, and the Operating Room archive. Everything is yours to read. Every download asks
            for an email and nothing more.
          </p>
          <p className="exec-serif text-[clamp(18px,2vw,22px)] leading-[1.5] text-[color:var(--lead-ink)] mt-5">
            Most firms hide their thinking behind a sales call. Ours is here. Read it first. If you
            find one decision worth making differently, the library has done its job. If you want us
            in the room when you make it, you know where to find us.
          </p>
        </motion.div>
      </section>

      {/* 2) SCORECARD — the centerpiece */}
      <section className="exec-container py-12">
        <motion.div
          {...fade}
          className="exec-card p-8 sm:p-12"
          style={{ borderLeft: `3px solid ${COPPER_ACCENT}` }}
          data-testid="resources-scorecard-block"
        >
          <div className="flex items-center gap-3">
            <ClipboardCheck className="h-5 w-5" style={{ color: COPPER_TEXT }} aria-hidden="true" />
            <p className="exec-eyebrow">Start here</p>
          </div>
          <h2 className="font-light text-[clamp(28px,3.4vw,40px)] leading-[1.15] mt-4 max-w-2xl">
            Score where you stand.
          </h2>
          <p className="exec-serif text-[clamp(17px,1.9vw,20px)] leading-[1.55] text-[color:var(--lead-ink)] mt-5 max-w-2xl">
            Ten questions, ten of the fifty we work through in a Sprint. You’ll get a personalized map
            of what to build, buy, or fix next, in minutes. No call required.
          </p>
          <Link
            to="/scorecard"
            data-testid="resources-take-scorecard"
            onClick={() => track("scorecard_open", { location: "resources" })}
            className="exec-btn exec-btn-primary mt-7"
          >
            Take the Readiness Scorecard <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </section>

      {/* 3) FRAMEWORKS */}
      <section className="exec-container pb-12">
        <motion.div {...fade} className="max-w-2xl">
          <p className="exec-eyebrow">Frameworks</p>
          <h2 className="font-light text-[clamp(28px,3.4vw,40px)] leading-[1.15] mt-4">
            The methods, not the marketing.
          </h2>
          <p className="exec-serif text-[17px] leading-[1.55] text-[color:var(--lead-ink)] mt-5">
            The frameworks behind every engagement. Yours to use whether or not you ever hire us.
          </p>
        </motion.div>

        <TopicFilter value={fwTopic} onChange={setFwTopic} testid="frameworks-topic-filter" />

        <div className="mt-9 grid gap-5 md:grid-cols-2" data-testid="resources-frameworks">
          {visibleFrameworks.map((f) => (
            <motion.div key={f.slug} {...fade} className="exec-card p-7 sm:p-8 flex flex-col" data-testid={`framework-card-${f.slug}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4" style={{ color: COPPER_TEXT }} aria-hidden="true" />
                  <p className="exec-eyebrow">{amp(f.kicker || f.tag || "Framework")}</p>
                </div>
                {f.topic && (
                  <span className="text-[10px] uppercase tracking-[0.14em] font-normal text-[color:var(--grey)]">{f.topic}</span>
                )}
              </div>
              <h3 className="font-light text-[clamp(20px,2.4vw,26px)] leading-[1.2] mt-3">{f.title}</h3>
              <p className="exec-serif text-[16px] leading-[1.5] text-[color:var(--lead-ink)] mt-3 flex-1">{f.description || f.summary}</p>
              <Link
                to={`/resources/frameworks/${f.slug}`}
                onClick={() => track("framework_open", { slug: f.slug })}
                data-testid={`framework-link-${f.slug}`}
                className="exec-link mt-6 self-start"
              >
                Read the framework <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          ))}
          {visibleFrameworks.length === 0 && (
            <p className="exec-serif text-[17px] text-[color:var(--lead-ink)]" data-testid="frameworks-empty">
              {gridFrameworks.length === 0 ? "Frameworks are publishing soon." : "No frameworks in this topic yet."}
            </p>
          )}
        </div>
      </section>

      {/* 4) FIELD NOTES */}
      <section className="exec-container pb-12" id="field-notes">
        <motion.div {...fade} className="max-w-2xl">
          <p className="exec-eyebrow">Field Notes</p>
          <h2 className="font-light text-[clamp(28px,3.4vw,40px)] leading-[1.15] mt-4">Field Notes.</h2>
          <p className="exec-serif text-[17px] leading-[1.55] text-[color:var(--lead-ink)] mt-5">
            Real decisions from the work: the questions founders and operators actually bring us, and
            how we worked them. Names withheld, the thinking isn’t.
          </p>
        </motion.div>

        <TopicFilter value={noteTopic} onChange={setNoteTopic} testid="field-notes-topic-filter" />

        {visibleNotes.length > 0 ? (
          <div className="mt-9 grid gap-5 md:grid-cols-2" data-testid="resources-field-notes">
            {visibleNotes.map((n) => {
              const external = !!n.canonical_url;
              const Wrapper = external ? "a" : Link;
              const wrapperProps = external
                ? { href: n.canonical_url, target: "_blank", rel: "noopener noreferrer" }
                : { to: `/resources/field-notes/${n.slug}` };
              return (
                <motion.div key={n.slug} {...fade} className="exec-card p-7 sm:p-8 flex flex-col" data-testid={`field-note-card-${n.slug}`}>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="exec-eyebrow">{amp(n.author || "Copper & Vine")}</span>
                    {fmtArticleDate(n.published_at) && (
                      <span className="text-[10px] tracking-[0.06em] text-[color:var(--grey)]">{fmtArticleDate(n.published_at)}</span>
                    )}
                    {n.topic && (
                      <span className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--grey)]">{n.topic}</span>
                    )}
                  </div>
                  <h3 className="font-light text-[clamp(20px,2.4vw,26px)] leading-[1.2] mt-3">{n.title}</h3>
                  <p className="exec-serif text-[16px] leading-[1.5] text-[color:var(--lead-ink)] mt-3 flex-1">{n.excerpt}</p>
                  <Wrapper
                    {...wrapperProps}
                    onClick={() => track("field_note_open", { slug: n.slug, external })}
                    data-testid={`field-note-link-${n.slug}`}
                    className="exec-link mt-6 self-start"
                  >
                    {external ? "Read on source" : "Read the note"}
                    {external ? <ExternalLink className="h-4 w-4" /> : <ArrowRight className="h-4 w-4" />}
                  </Wrapper>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <motion.div {...fade} className="mt-8 exec-card p-8 sm:p-10" data-testid="field-notes-empty">
            <p className="exec-serif text-[17px] text-[color:var(--lead-ink)] max-w-xl">
              {fieldNotes.length === 0
                ? "The first Field Notes are being prepared for publication. They will appear here as a readable, on-site library."
                : "No Field Notes in this topic yet."}
            </p>
            <Link to="/scorecard" className="exec-link mt-5" data-testid="field-notes-scorecard-link">
              Score where you stand while you wait <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        )}
      </section>

      {/* 5) OPERATOR PACKET */}
      <section className="exec-container pb-12">
        <motion.div {...fade} className="max-w-2xl">
          <p className="exec-eyebrow">The Operating Room</p>
          <h2 className="font-light text-[clamp(28px,3.4vw,40px)] leading-[1.15] mt-4">The Operating Room.</h2>
          <p className="exec-serif text-[17px] leading-[1.55] text-[color:var(--lead-ink)] mt-5">
            One specific insight from the work, monthly. No pitch. Read it before you decide anything.
          </p>
        </motion.div>

        <motion.div {...fade} className="mt-7">
          <PacketSignup />
        </motion.div>

        <motion.div {...fade} className="mt-6">
          <p className="exec-serif text-[16px] text-[color:var(--lead-ink)] max-w-xl" data-testid="packet-archive-note">
            Each month’s insight becomes a Field Note.{" "}
            <a href="#field-notes" className="exec-link inline">read past issues above</a>.
          </p>
        </motion.div>
      </section>

      {/* 6) CLOSE */}
      <section className="exec-container pb-24">
        <div className="exec-rule mb-14" />
        <motion.div {...fade} className="max-w-2xl">
          <h2 className="font-light text-[clamp(28px,3.4vw,40px)] leading-[1.15]">You’ve seen how we think.</h2>
          <p className="exec-serif text-[17px] leading-[1.55] text-[color:var(--lead-ink)] mt-5">
            If a decision in front of you is worth a senior read, request the brief. Or score where you
            stand first.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-4">
            <button
              type="button"
              onClick={() => {
                track("brief_open", { location: "resources-close" });
                openBrief("general");
              }}
              data-testid="resources-request-brief"
              className="exec-btn exec-btn-primary"
            >
              Request the brief
            </button>
            <Link to="/scorecard" data-testid="resources-close-scorecard" className="exec-btn exec-btn-secondary">
              Take the Scorecard
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
